import { useState } from "react";
import { ChevronDown } from "lucide-react";
import PublicPageLayout from "@/components/PublicPageLayout";

const CATEGORIES = [
  {
    title: "The assessment",
    testid: "faq-cat-assessment",
    items: [
      {
        q: "How does the 12-step assessment work?",
        a: "The assessment walks you through twelve focused topics — your current situation, work history, skills, education, hobbies, personality, physical constraints, salary expectations, financial position, location, retraining capacity and career goals. Each step takes 2–5 minutes, your progress saves automatically, and you can stop and resume any time. At the end you review everything before generating your report.",
      },
      {
        q: "How long does it take to complete?",
        a: "Most people finish in 25–40 minutes spread over one or two sittings. There's no penalty for taking your time — your draft is saved after every step.",
      },
      {
        q: "Can I update my answers after submitting?",
        a: "Yes. You can revisit the assessment, edit any step, and re-generate your report. We keep your previous reports so you can compare.",
      },
    ],
  },
  {
    title: "AI recommendations",
    testid: "faq-cat-ai",
    items: [
      {
        q: "How do the AI recommendations actually work?",
        a: "We send a structured version of your assessment to three different large language models in parallel — OpenAI GPT-4o, Anthropic Claude Sonnet and Google Gemini. Each model independently proposes pathways. Our consensus layer then merges, ranks and de-duplicates their suggestions so you see what the models agree on, not just one model's opinion.",
      },
      {
        q: "Why use three models instead of one?",
        a: "Each model has different blind spots and biases. Using three lets us highlight pathways that are robust across multiple AI 'opinions' and flag suggestions only one model proposed. It's slower and more expensive, but the result is meaningfully more reliable.",
      },
      {
        q: "Can the AI be wrong?",
        a: "Yes — and we tell you when it disagrees with itself. Treat the recommendations as a well-researched starting point, not a verdict. A human conversation with a career counsellor or trusted mentor is still valuable.",
      },
    ],
  },
  {
    title: "Subscription & pricing",
    testid: "faq-cat-subscription",
    items: [
      {
        q: "How much does it cost?",
        a: "Career Transition Membership is AUD $50 per month + 10% GST = $55 per month including GST, with a minimum three-month commitment ($165 inc. GST total to start). After three months you continue month-to-month and can cancel any time.",
      },
      {
        q: "What's My Archive and how does it cost?",
        a: "My Archive is a second plan for when you've finished the active programme but want to keep your assessment, AI report and PDF résumé on file for future use. It's AUD $10/month + GST, billed once a year as a single $132 incl. GST charge ($120 + $12 GST). Available after your first three months of membership.",
      },
      {
        q: "What's included in the subscription?",
        a: "Unlimited access to your full career report, all five pathway recommendations, the Australian government support screener, the job matching dashboard with five role buckets, saved jobs, and application tracking.",
      },
      {
        q: "Is the assessment free?",
        a: "Yes. Creating an account, completing the 12-step assessment and seeing a teaser of your results is completely free. You only pay if you want the full report and ongoing job matching.",
      },
      {
        q: "Why a three-month minimum?",
        a: "Career transitions don't happen overnight. Three months is the minimum window where most users see real movement — finishing the assessment, applying to roles, hearing back, and adjusting course. We'd rather charge fairly upfront than dress up a higher monthly price.",
      },
    ],
  },
  {
    title: "Cancellation",
    testid: "faq-cat-cancellation",
    items: [
      {
        q: "How do I cancel?",
        a: "Open Account → Subscription → Cancel. After your three-month minimum your subscription stops at the end of the current billing period. There are no cancellation fees, no phone calls, and no retention emails.",
      },
      {
        q: "What happens to my data when I cancel?",
        a: "Your assessment and report stay in your account so you can return later. Job matching pauses. You can permanently delete everything from Account → Privacy any time.",
      },
      {
        q: "Do you offer refunds?",
        a: "If something genuinely isn't working for you within the first 14 days, contact us — we handle refund requests case-by-case, fairly.",
      },
    ],
  },
  {
    title: "Job matching",
    testid: "faq-cat-jobs",
    items: [
      {
        q: "Where do the job listings come from?",
        a: "Active job sources include Adzuna (live), with planned connectors for Seek, Indeed, LinkedIn and the Australian government jobs board. Listings are filtered against your assessment so you only see roles relevant to your pathways.",
      },
      {
        q: "What are the five role buckets?",
        a: "Apply Now (you're ready today), Lateral Moves (similar role, AI-resilient industry), Upskill Soon (short course away), Fresh Start (retrain into a new field) and Step Back (lower-pressure roles for transition periods).",
      },
      {
        q: "Will you apply to jobs for me?",
        a: "No. We surface relevant roles, track which ones you've applied to, and keep notes — but the application itself is yours. We think automated mass-applications hurt your odds and the industry.",
      },
    ],
  },
  {
    title: "Government support guidance",
    testid: "faq-cat-government",
    items: [
      {
        q: "What government support do you cover?",
        a: "We screen against major Australian federal and state programs including JobSeeker Payment, Skills Checkpoint for Older Workers, Workforce Australia, Fee-Free TAFE, state-based retraining grants, and disability/carer-related supports where relevant.",
      },
      {
        q: "Are you affiliated with the Australian government?",
        a: "No. We are an independent service. We surface the programs you may be eligible for and link directly to the official application pages — we never collect your application data on their behalf.",
      },
      {
        q: "Can you guarantee I'll get a payment?",
        a: "No. Eligibility is decided by the relevant agency (Services Australia, your state government, etc.). Our screener is an indicator, not an approval.",
      },
    ],
  },
  {
    title: "Privacy & sensitive data",
    testid: "faq-cat-privacy",
    items: [
      {
        q: "What data do you collect?",
        a: "Your account info (email, name), your assessment answers, jobs you save or apply to, and basic billing metadata from Stripe. We don't collect Tax File Numbers, Medicare numbers, or government IDs — please don't include them in free-text answers.",
      },
      {
        q: "Is my assessment shared with the LLM providers?",
        a: "Yes — your structured assessment is sent to OpenAI, Anthropic and Google to generate your report. All three providers have enterprise agreements that prohibit training on inputs. We don't send your name, email or any direct identifier with the prompt.",
      },
      {
        q: "Can I delete everything?",
        a: "Yes. Account → Privacy → Delete my data wipes your assessment, reports, saved jobs and account. Backup retention is up to 30 days, after which it's permanently gone.",
      },
      {
        q: "Where is my data stored?",
        a: "Application data is stored in MongoDB Atlas in an Australian-region cluster. Stripe handles payment data on PCI-compliant infrastructure.",
      },
    ],
  },
];

function FaqItem({ q, a, idx, catIdx }) {
  const [open, setOpen] = useState(false);
  const testid = `faq-item-${catIdx}-${idx}`;
  return (
    <div
      className="border border-slate-200 rounded-2xl bg-white overflow-hidden"
      data-testid={testid}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-slate-50 transition-colors"
        data-testid={`${testid}-toggle`}
      >
        <span className="text-base font-medium text-slate-900">{q}</span>
        <ChevronDown
          className={`h-4 w-4 text-slate-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div
          className="px-5 pb-5 text-slate-600 leading-relaxed text-base border-t border-slate-100"
          data-testid={`${testid}-answer`}
        >
          <p className="pt-4">{a}</p>
        </div>
      )}
    </div>
  );
}

export default function FAQ() {
  return (
    <PublicPageLayout
      eyebrow="FAQ"
      title="Frequently asked questions"
      intro="Short, honest answers to the things people actually ask. If something's missing, send us a note via the contact page."
      testid="faq-page"
    >
      {CATEGORIES.map((cat, ci) => (
        <section key={cat.title} data-testid={cat.testid} className="space-y-4">
          <h2 className="text-xl sm:text-2xl font-medium text-slate-900 tracking-tight">
            {cat.title}
          </h2>
          <div className="space-y-3">
            {cat.items.map((it, ii) => (
              <FaqItem key={it.q} q={it.q} a={it.a} idx={ii} catIdx={ci} />
            ))}
          </div>
        </section>
      ))}
    </PublicPageLayout>
  );
}
